'use client';

import { useEffect, useState } from 'react';
import { THEME_MODE_KEY } from '../lib/security/themeInitScript';
import { cn } from '../lib/cn';
import {
  type ThemeMode,
  applyTheme,
  getStoredThemeMode,
  saveThemeMode,
  subscribeSystemThemeChange,
} from '../lib/theme';
import { useUpdateMyProfileMutation } from '../hooks/queries/useMember';

export type { ThemeMode } from '../lib/theme';
export { getStoredThemeMode, saveThemeMode } from '../lib/theme';

type ThemeToggleProps = {
  token?: string | null;
  persistPreference?: boolean;
  initialMode?: ThemeMode;
  framed?: boolean;
};

const getCurrentDocumentThemeMode = (): ThemeMode => {
  if (typeof document === 'undefined') return 'LIGHT';
  return document.documentElement.getAttribute('data-theme') === 'night' ? 'DARK' : 'LIGHT';
};

/** Compact 3-state toggle rendered in the top-right header. */
export const ThemeToggle = ({ token, persistPreference = true, initialMode, framed = true }: ThemeToggleProps = {}) => {
  const [mode, setMode] = useState<ThemeMode>('SYSTEM');
  const updateMutation = useUpdateMyProfileMutation(token ?? '');

  useEffect(() => {
    if (!persistPreference) {
      const next = initialMode ?? getCurrentDocumentThemeMode();
      setMode(next);
      applyTheme(next);
      return;
    }

    const stored = getStoredThemeMode();
    setMode(stored);
    applyTheme(stored);

    // React to OS theme changes when mode is SYSTEM
    const unsubscribe = subscribeSystemThemeChange(() => {
      if (getStoredThemeMode() === 'SYSTEM') {
        applyTheme('SYSTEM');
      }
    });

    // Cross-tab sync — keep the toggle indicator and theme in sync
    const handleStorage = (event: StorageEvent) => {
      if (event.key === THEME_MODE_KEY && event.newValue) {
        const next: ThemeMode = event.newValue === 'LIGHT' || event.newValue === 'DARK' ? event.newValue : 'SYSTEM';
        setMode(next);
        applyTheme(next);
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      unsubscribe();
      window.removeEventListener('storage', handleStorage);
    };
  }, [initialMode, persistPreference]);

  const cycleMode = () => {
    const next: ThemeMode = token
      ? mode === 'SYSTEM'
        ? 'LIGHT'
        : mode === 'LIGHT'
          ? 'DARK'
          : 'SYSTEM'
      : mode === 'LIGHT'
        ? 'DARK'
        : 'LIGHT';
    const previous = mode;
    setMode(next);
    if (persistPreference) {
      saveThemeMode(next);
    } else {
      applyTheme(next);
    }

    if (token && persistPreference) {
      void updateMutation.mutateAsync({ themeMode: next }).catch(() => {
        // Rollback on API failure
        setMode(previous);
        if (persistPreference) {
          saveThemeMode(previous);
        } else {
          applyTheme(previous);
        }
      });
    }
  };

  return (
    <button
      type="button"
      className={cn(
        'btn h-9 min-h-0 w-9 p-0 transition',
        framed
          ? 'btn-circle border shadow-xs backdrop-blur hover:shadow'
          : 'btn-ghost rounded-none border-0 bg-transparent text-[var(--color-fg-muted)] shadow-none hover:bg-base-200/70 hover:text-base-content',
      )}
      style={
        framed
          ? {
              background: 'color-mix(in oklch, var(--color-bg-elevated) 90%, transparent)',
              borderColor: 'var(--color-border)',
              color: 'var(--color-fg-muted)',
            }
          : undefined
      }
      aria-label="Toggle theme"
      onClick={cycleMode}
      title={mode === 'SYSTEM' ? 'System' : mode === 'LIGHT' ? 'Light' : 'Dark'}
    >
      {mode === 'DARK' ? (
        // Moon icon (dark mode active)
        <svg className="h-5 w-5 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path d="M21.64,13a1,1,0,0,0-1.05-.14,8.05,8.05,0,0,1-3.37.73A8.15,8.15,0,0,1,9.08,5.49a8.59,8.59,0,0,1,.25-2A1,1,0,0,0,8,2.36,10.14,10.14,0,1,0,22,14.05,1,1,0,0,0,21.64,13Zm-9.5,6.69A8.14,8.14,0,0,1,7.08,5.22v.27A10.15,10.15,0,0,0,17.22,15.63a9.79,9.79,0,0,0,2.1-.22A8.11,8.11,0,0,1,12.14,19.73Z" />
        </svg>
      ) : mode === 'LIGHT' ? (
        // Sun icon (light mode active)
        <svg className="h-5 w-5 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path d="M5.64,17l-.71.71a1,1,0,0,0,0,1.41,1,1,0,0,0,1.41,0l.71-.71A1,1,0,0,0,5.64,17ZM5,12a1,1,0,0,0-1-1H3a1,1,0,0,0,0,2H4A1,1,0,0,0,5,12Zm7-7a1,1,0,0,0,1-1V3a1,1,0,0,0-2,0V4A1,1,0,0,0,12,5ZM5.64,7.05a1,1,0,0,0,.7.29,1,1,0,0,0,.71-.29,1,1,0,0,0,0-1.41l-.71-.71A1,1,0,0,0,4.93,6.34Zm12,.29a1,1,0,0,0,.7-.29l.71-.71a1,1,0,1,0-1.41-1.41L17,5.64a1,1,0,0,0,0,1.41A1,1,0,0,0,17.66,7.34ZM21,11H20a1,1,0,0,0,0,2h1a1,1,0,0,0,0-2Zm-9,8a1,1,0,0,0-1,1v1a1,1,0,0,0,2,0V20A1,1,0,0,0,12,19ZM18.36,17A1,1,0,0,0,17,18.36l.71.71a1,1,0,0,0,1.41,0,1,1,0,0,0,0-1.41ZM12,6.5A5.5,5.5,0,1,0,17.5,12,5.51,5.51,0,0,0,12,6.5Zm0,9A3.5,3.5,0,1,1,15.5,12,3.5,3.5,0,0,1,12,15.5Z" />
        </svg>
      ) : (
        // Monitor/auto icon (system mode active)
        <svg
          className="h-5 w-5 stroke-current fill-none"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <path d="M8 21h8M12 17v4" />
        </svg>
      )}
    </button>
  );
};
